import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import EditProcessingConfigModal from "../components/grants/processing-configs/EditProcessingConfigModal";
import { EmptyState } from "../components/common/EmptyState";
import { SettingsIcon, PlusIcon } from "../components/icons";

/**
 * Maps API error messages to user-friendly Polish messages.
 * This prevents information disclosure by hiding internal details (UUIDs, constraint names).
 */
const errorMessages: Record<string, string> = {
  // Not found errors
  "Konfiguracja nie znaleziona": "Konfiguracja nie zostala znaleziona",
  // Conflict errors
  "Konfiguracja o tej nazwie juz istnieje":
    "Konfiguracja o tej nazwie juz istnieje",
  "Nie mozna usunac konfiguracji w uzyciu":
    "Nie mozna usunac konfiguracji, ktora jest uzywana",
  "Nie mozna usunac domyslnej konfiguracji":
    "Nie mozna usunac domyslnej konfiguracji. Najpierw ustaw inna jako domyslna.",
  // Validation errors
  "Nieprawidlowy format UUID": "Blad walidacji danych",
  "Wymagana minimum 1 grupa slow kluczowych":
    "Wymagana minimum 1 grupa slow kluczowych",
  "Kazda grupa musi zawierac minimum 1 slowo kluczowe":
    "Kazda grupa musi zawierac minimum 1 slowo kluczowe",
};

/**
 * Extracts user-friendly error message from API error response.
 * Logs original error for debugging while returning sanitized message to user.
 */
const getErrorMessage = (error: any): string => {
  const rawMessage = error.response?.data?.message || error.message;

  // Normalize message to string (NestJS validation errors return array)
  let originalMessage: string;
  if (Array.isArray(rawMessage)) {
    originalMessage = rawMessage[0] || "";
  } else if (typeof rawMessage === "string") {
    originalMessage = rawMessage;
  } else if (rawMessage && typeof rawMessage === "object") {
    originalMessage = rawMessage.message || JSON.stringify(rawMessage);
  } else {
    originalMessage = "";
  }

  // Log original error for debugging (includes internal details) - dev only
  if (process.env.NODE_ENV !== "production") {
    console.error("[ProcessingConfigs] API Error:", {
      status: error.response?.status,
      message: originalMessage,
      data: error.response?.data,
    });
  }

  // Check for known error messages
  for (const [pattern, friendlyMessage] of Object.entries(errorMessages)) {
    if (originalMessage.toLowerCase().includes(pattern.toLowerCase())) {
      return friendlyMessage;
    }
  }

  // Generic fallback - never expose raw error message
  if (error.response?.status >= 500) {
    return "Blad serwera. Sprobuj ponownie pozniej.";
  }
  if (error.response?.status === 404) {
    return "Nie znaleziono zasobu.";
  }
  if (error.response?.status === 409) {
    return "Operacja nie moze byc wykonana z powodu konfliktu danych.";
  }
  if (error.response?.status === 400) {
    return "Blad walidacji danych.";
  }

  return "Wystapil blad. Sprobuj ponownie.";
};

interface ProcessingConfig {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  usageCount: number;
  usageBreakdown: {
    sources: number;
    operators: number;
    programs: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface ProcessingConfigsResponse {
  items: ProcessingConfig[];
  total: number;
}

interface ProcessingConfigRowProps {
  config: ProcessingConfig;
  onEdit: (config: ProcessingConfig) => void;
  onDuplicate: (config: ProcessingConfig) => void;
  onDelete: (config: ProcessingConfig) => void;
  onSetDefault: (config: ProcessingConfig) => void;
  isAdmin: boolean;
  deleteMutationVars: string | undefined;
  duplicateMutationVars: string | undefined;
  setDefaultMutationVars: string | undefined;
  deleteMutationIsPending: boolean;
  duplicateMutationIsPending: boolean;
  setDefaultMutationIsPending: boolean;
  formatUsageBreakdown: (breakdown: ProcessingConfig["usageBreakdown"]) => string;
}

/**
 * Memoized table row component for a processing config.
 * Prevents unnecessary re-renders of individual rows when the list updates.
 */
const ProcessingConfigRow = React.memo(function ProcessingConfigRow({
  config,
  onEdit,
  onDuplicate,
  onDelete,
  onSetDefault,
  isAdmin,
  deleteMutationVars,
  duplicateMutationVars,
  setDefaultMutationVars,
  deleteMutationIsPending,
  duplicateMutationIsPending,
  setDefaultMutationIsPending,
  formatUsageBreakdown,
}: ProcessingConfigRowProps) {
  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-4 py-4">
        <div className="text-sm font-medium text-neutral-900">
          {config.name}
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="text-sm text-neutral-500 max-w-xs truncate">
          {config.description || "-"}
        </div>
      </td>
      <td className="px-4 py-4 text-center">
        {config.isDefault ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            Domyślna
          </span>
        ) : isAdmin ? (
          <button
            onClick={() => onSetDefault(config)}
            className="text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            title="Ustaw jako domyślną"
            disabled={setDefaultMutationIsPending}
          >
            {setDefaultMutationIsPending && setDefaultMutationVars === config.id ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></span>
                Ustawiam...
              </>
            ) : (
              "Ustaw"
            )}
          </button>
        ) : (
          <span className="text-sm text-neutral-400">-</span>
        )}
      </td>
      <td className="px-4 py-4 text-right">
        <div className="text-sm text-neutral-900">
          {config.usageCount}
        </div>
        <div className="text-xs text-neutral-500">
          {formatUsageBreakdown(config.usageBreakdown)}
        </div>
      </td>
      {isAdmin && (
        <td className="px-4 py-4 whitespace-nowrap text-right">
          <button
            onClick={() => onEdit(config)}
            className="text-primary-600 hover:text-primary-800 mr-3 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={`Edytuj konfigurację ${config.name}`}
            disabled={deleteMutationIsPending || duplicateMutationIsPending || setDefaultMutationIsPending}
          >
            Edytuj
          </button>
          <button
            onClick={() => onDuplicate(config)}
            className="text-primary-600 hover:text-primary-800 mr-3 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            aria-label={`Duplikuj konfigurację ${config.name}`}
            disabled={deleteMutationIsPending || duplicateMutationIsPending || setDefaultMutationIsPending}
          >
            {duplicateMutationIsPending && duplicateMutationVars === config.id ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></span>
                Duplikuję...
              </>
            ) : (
              "Duplikuj"
            )}
          </button>
          <button
            onClick={() => onDelete(config)}
            className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            disabled={config.usageCount > 0 || config.isDefault || deleteMutationIsPending || duplicateMutationIsPending || setDefaultMutationIsPending}
            aria-label={`Usuń konfigurację ${config.name}`}
          >
            {deleteMutationIsPending && deleteMutationVars === config.id ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-1"></span>
                Usuwam...
              </>
            ) : (
              "Usuń"
            )}
          </button>
        </td>
      )}
    </tr>
  );
});

/**
 * Skeleton row component for loading state.
 * Shows animated placeholder row while data is being fetched.
 */
const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-4 py-4">
      <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
    </td>
    <td className="px-4 py-4 text-center">
      <div className="h-4 bg-neutral-200 rounded w-12 mx-auto"></div>
    </td>
    <td className="px-4 py-4 text-right">
      <div className="h-4 bg-neutral-200 rounded w-8 ml-auto mb-1"></div>
      <div className="h-3 bg-neutral-100 rounded w-20 ml-auto"></div>
    </td>
    <td className="px-4 py-4 text-right">
      <div className="flex justify-end gap-3">
        <div className="h-4 bg-neutral-200 rounded w-12"></div>
        <div className="h-4 bg-neutral-200 rounded w-12"></div>
        <div className="h-4 bg-neutral-200 rounded w-10"></div>
      </div>
    </td>
  </tr>
);

/**
 * Page for managing ProcessingConfig entities.
 * Shows configs with their usage counts and allows CRUD operations for admins.
 */
const GrantsProcessingConfigs: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } =
    useQuery<ProcessingConfigsResponse>({
      queryKey: ["grants-processing-configs"],
      queryFn: async () => {
        const { data } = await apiClient.get(
          "/grants/admin/processing-configs"
        );
        return data;
      },
    });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/grants/admin/processing-configs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-processing-configs"] });
    },
    onError: (error: any) => {
      alert(getErrorMessage(error));
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(
        `/grants/admin/processing-configs/${id}/duplicate`
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["grants-processing-configs"] });
      setEditingConfigId(data.id);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.patch(
        `/grants/admin/processing-configs/${id}/set-default`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-processing-configs"] });
    },
  });

  const handleDelete = useCallback((config: ProcessingConfig) => {
    if (config.usageCount > 0) {
      alert(
        `Nie można usunąć konfiguracji "${config.name}" - jest używana przez ${config.usageCount} elementów.`
      );
      return;
    }
    if (config.isDefault) {
      alert(
        `Nie można usunąć domyślnej konfiguracji "${config.name}". Najpierw ustaw inną jako domyślną.`
      );
      return;
    }
    if (
      window.confirm(
        `Czy na pewno chcesz usunąć konfigurację "${config.name}"?`
      )
    ) {
      deleteMutation.mutate(config.id);
    }
  }, [deleteMutation]);

  const handleDuplicate = useCallback((config: ProcessingConfig) => {
    duplicateMutation.mutate(config.id);
  }, [duplicateMutation]);

  const handleSetDefault = useCallback((config: ProcessingConfig) => {
    if (config.isDefault) return;
    if (
      window.confirm(
        `Czy na pewno chcesz ustawić "${config.name}" jako domyślną konfigurację?`
      )
    ) {
      setDefaultMutation.mutate(config.id);
    }
  }, [setDefaultMutation]);

  const handleEdit = useCallback((config: ProcessingConfig) => {
    setEditingConfigId(config.id);
  }, []);

  const formatUsageBreakdown = useCallback((breakdown: ProcessingConfig["usageBreakdown"]) => {
    const parts = [];
    if (breakdown.sources > 0) parts.push(`${breakdown.sources} źródeł`);
    if (breakdown.operators > 0) parts.push(`${breakdown.operators} operatorów`);
    if (breakdown.programs > 0) parts.push(`${breakdown.programs} programów`);
    return parts.length > 0 ? parts.join(", ") : "-";
  }, []);

  return (
    <Layout>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:bg-white focus:p-4 focus:text-primary-600 focus:underline"
      >
        Przejdź do głównej treści
      </a>
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <nav className="text-sm text-neutral-500">
          <Link to="/grants/calls" className="hover:text-primary-600">
            Grants
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="text-neutral-900">Konfiguracje przetwarzania</span>
        </nav>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Konfiguracje przetwarzania
            </h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Ustawienia ekstrakcji treści, klasyfikacji i ekstrakcji danych
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={deleteMutation.isPending || duplicateMutation.isPending || setDefaultMutation.isPending}
            >
              Dodaj konfigurację
            </button>
          )}
        </div>

        <div id="main-content" className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Błąd podczas pobierania danych</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-primary-600 hover:text-primary-800"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Nazwa
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Opis
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Domyślna
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Użycie
                    </th>
                    {canWrite && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                        Akcje
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {isLoading ? (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} />
                      ))}
                    </>
                  ) : (
                    data?.items?.map((config) => (
                      <ProcessingConfigRow
                        key={config.id}
                        config={config}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                        onSetDefault={handleSetDefault}
                        isAdmin={canWrite}
                        deleteMutationVars={deleteMutation.variables}
                        duplicateMutationVars={duplicateMutation.variables}
                        setDefaultMutationVars={setDefaultMutation.variables}
                        deleteMutationIsPending={deleteMutation.isPending}
                        duplicateMutationIsPending={duplicateMutation.isPending}
                        setDefaultMutationIsPending={setDefaultMutation.isPending}
                        formatUsageBreakdown={formatUsageBreakdown}
                      />
                    ))
                  )}
                </tbody>
              </table>

              {!isLoading && (!data?.items || data.items.length === 0) && (
                <EmptyState
                  icon={<SettingsIcon className="h-12 w-12 mx-auto" />}
                  title="Brak konfiguracji przetwarzania"
                  description="Konfiguracje definiuja jak artykuly sa klasyfikowane i analizowane. Rozpocznij od utworzenia pierwszej konfiguracji."
                  action={canWrite ? {
                    label: "Utworz pierwsza konfiguracje",
                    onClick: () => setIsAddModalOpen(true),
                    icon: <PlusIcon className="-ml-0.5 h-5 w-5" />,
                  } : undefined}
                  className="p-6"
                />
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <EditProcessingConfigModal
          isOpen={isAddModalOpen || !!editingConfigId}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingConfigId(null);
          }}
          configId={editingConfigId}
        />
      </div>
    </Layout>
  );
};

export default GrantsProcessingConfigs;
